/**
 * Core Animation Engine for Flowi
 * Manages all animations in the system
 */

import {
  AnimationConfig,
  AnimationState,
  AnimationTarget,
  PropertyPath,
  PropertyAnimation,
  AnimationEvent,
  AnimationGroup,
  EasingFunction,
  EasingPreset
} from './animationTypes';
import { easings } from './easings';
import { getNestedProperty, setNestedProperty } from './utils';

export class AnimationEngine {
  private animations: Map<string, AnimationState> = new Map();
  private groups: Map<string, AnimationGroup> = new Map();
  private animationFrame: number | null = null;
  private lastTime: number = 0;
  private listeners: Map<string, Set<(event: AnimationEvent) => void>> = new Map();
  private nextId: number = 0;

  constructor() {
    console.log(this.lastTime);
    // Initialize event listener maps
    ['start', 'update', 'complete', 'stop', 'pause', 'resume'].forEach(type => {
      this.listeners.set(type, new Set());
    });
  }

  /**
   * Start a new animation
   */
  public animate(
    target: AnimationTarget,
    properties: Record<PropertyPath, number>,
    config: AnimationConfig = {}
  ): string {
    const id = this.generateId();
    const state = this.createAnimationState(id, target, properties, config);

    this.animations.set(id, state);

    if (state.config.delay > 0) {
      setTimeout(() => this.startAnimation(id), state.config.delay);
    } else {
      this.startAnimation(id);
    }

    return id;
  }

  /**
   * Stop an animation
   */
  public stop(id: string, jumpToEnd: boolean = false): void {
    const animation = this.animations.get(id);
    if (!animation || animation.state === 'completed') return;

    if (jumpToEnd) {
      // Set all properties to their target values
      animation.properties.forEach((prop, path) => {
        setNestedProperty(animation.target, path, prop.to);
        prop.current = prop.to;
      });
    }

    animation.state = 'completed';
    this.emit('stop', { animationId: id, target: animation.target });

    if (animation.config.onStop) {
      animation.config.onStop();
    }

    this.animations.delete(id);
    this.checkAnimationLoop();
  }

  /**
   * Pause an animation
   */
  public pause(id: string): void {
    const animation = this.animations.get(id);
    if (!animation || animation.state !== 'running') return;

    animation.state = 'paused';
    animation.pauseTime = performance.now();
    this.emit('pause', { animationId: id, target: animation.target });
  }

  /**
   * Resume a paused animation
   */
  public resume(id: string): void {
    const animation = this.animations.get(id);
    if (!animation || animation.state !== 'paused') return;

    if (animation.pauseTime) {
      animation.pausedDuration += performance.now() - animation.pauseTime;
      animation.pauseTime = undefined;
    }

    animation.state = 'running';
    this.emit('resume', { animationId: id, target: animation.target });
    this.startAnimationLoop();
  }

  /**
   * Stop all animations
   */
  public stopAll(jumpToEnd: boolean = false): void {
    const ids = Array.from(this.animations.keys());
    ids.forEach(id => this.stop(id, jumpToEnd));
  }

  /**
   * Pause all animations
   */
  public pauseAll(): void {
    this.animations.forEach((_, id) => this.pause(id));
  }

  /**
   * Resume all animations
   */
  public resumeAll(): void {
    this.animations.forEach((_, id) => this.resume(id));
  }

  /**
   * Create an animation group
   */
  public group(
    animations: Array<{
      target: AnimationTarget;
      properties: Record<PropertyPath, number>;
      config?: AnimationConfig;
    }>,
    mode: 'parallel' | 'sequence' | 'stagger' = 'parallel',
    staggerDelay: number = 50
  ): string {
    const groupId = this.generateId();
    const animationIds: string[] = [];

    const group: AnimationGroup = {
      id: groupId,
      animations: animationIds,
      config: { mode, staggerDelay }
    };

    this.groups.set(groupId, group);

    // Create animations based on mode
    animations.forEach((anim, index) => {
      let delay = anim.config?.delay || 0;

      if (mode === 'sequence' && index > 0) {
        // Calculate total duration of previous animations
        const prevDuration = animations.slice(0, index).reduce((total, a) =>
          total + (a.config?.duration || 1000) + (a.config?.delay || 0), 0);
        delay += prevDuration;
      } else if (mode === 'stagger') {
        delay += index * staggerDelay;
      }

      const id = this.animate(anim.target, anim.properties, {
        ...anim.config,
        delay
      });

      animationIds.push(id);
    });

    return groupId;
  }

  /**
   * Add event listener
   */
  public on(event: string, callback: (event: AnimationEvent) => void): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.add(callback);
    }
  }

  /**
   * Remove event listener
   */
  public off(event: string, callback: (event: AnimationEvent) => void): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  /**
   * Get animation by ID
   */
  public getAnimation(id: string): AnimationState | undefined {
    return this.animations.get(id);
  }

  /**
   * Check if any animations are running
   */
  public isAnimating(): boolean {
    return Array.from(this.animations.values()).some(
      anim => anim.state === 'running'
    );
  }

  // Private methods

  private createAnimationState(
    id: string,
    target: AnimationTarget,
    properties: Record<PropertyPath, number>,
    config: AnimationConfig
  ): AnimationState {
    const props = new Map<PropertyPath, PropertyAnimation>();

    // Initialize property animations
    Object.entries(properties).forEach(([path, to]) => {
      const from = getNestedProperty(target, path) || 0;
      props.set(path, { from, to, current: from });
    });

    return {
      id,
      target,
      properties: props,
      config: {
        duration: config.duration ?? 1000,
        delay: config.delay ?? 0,
        easing: config.easing ?? 'easeOutQuad',
        repeat: config.repeat ?? 0,
        repeatDelay: config.repeatDelay ?? 0,
        yoyo: config.yoyo ?? false,
        onStart: config.onStart,
        onUpdate: config.onUpdate,
        onComplete: config.onComplete,
        onStop: config.onStop
      },
      state: 'idle',
      startTime: 0,
      pausedDuration: 0,
      repeatCount: 0,
      isYoyo: false
    };
  }

  private startAnimation(id: string): void {
    const animation = this.animations.get(id);
    if (!animation) return;

    animation.state = 'running';
    animation.startTime = performance.now();

    this.emit('start', { animationId: id, target: animation.target });

    if (animation.config.onStart) {
      animation.config.onStart();
    }

    this.startAnimationLoop();
  }

  private startAnimationLoop(): void {
    if (this.animationFrame !== null) return;

    const animate = (currentTime: number) => {
      // const deltaTime = currentTime - this.lastTime;
      this.lastTime = currentTime;

      this.updateAnimations(currentTime);

      if (this.isAnimating()) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.stopAnimationLoop();
      }
    };

    this.lastTime = performance.now();
    this.animationFrame = requestAnimationFrame(animate);
  }

  private stopAnimationLoop(): void {
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  private checkAnimationLoop(): void {
    if (!this.isAnimating()) {
      this.stopAnimationLoop();
    }
  }

  private updateAnimations(currentTime: number): void {
    this.animations.forEach((animation, id) => {
      if (animation.state !== 'running') return;

      const elapsed = currentTime - animation.startTime - animation.pausedDuration;
      let progress = Math.min(elapsed / animation.config.duration, 1);

      // Apply easing
      const easedProgress = this.applyEasing(progress, animation.config.easing);

      // Update properties
      animation.properties.forEach((prop, path) => {
        const value = this.interpolate(
          prop.from,
          prop.to,
          animation.isYoyo ? 1 - easedProgress : easedProgress
        );

        prop.current = value;
        setNestedProperty(animation.target, path, value);
      });

      // Emit update event
      this.emit('update', {
        animationId: id,
        target: animation.target,
        progress: animation.isYoyo ? 1 - progress : progress
      });

      if (animation.config.onUpdate) {
        animation.config.onUpdate(animation.isYoyo ? 1 - progress : progress);
      }

      // Check if animation is complete
      if (progress >= 1) {
        this.handleAnimationComplete(id);
      }
    });
  }

  private handleAnimationComplete(id: string): void {
    const animation = this.animations.get(id);
    if (!animation) return;

    // Handle repeat
    if (animation.repeatCount < animation.config.repeat || animation.config.repeat === -1) {
      animation.repeatCount++;

      if (animation.config.yoyo) {
        animation.isYoyo = !animation.isYoyo;
      } else {
        // Reset properties to start values
        animation.properties.forEach((prop) => {
          prop.current = prop.from;
        });
      }

      animation.startTime = performance.now() + animation.config.repeatDelay;
      animation.pausedDuration = 0;
      return;
    }

    // Animation is truly complete
    animation.state = 'completed';

    this.emit('complete', { animationId: id, target: animation.target });

    if (animation.config.onComplete) {
      animation.config.onComplete();
    }

    this.animations.delete(id);
    this.checkAnimationLoop();
  }

  private applyEasing(t: number, easing: EasingFunction | EasingPreset): number {
    if (typeof easing === 'function') {
      return easing(t);
    }

    const easingFn = easings[easing];
    return easingFn ? easingFn(t) : t;
  }

  private interpolate(from: number, to: number, progress: number): number {
    return from + (to - from) * progress;
  }

  private emit(event: string, data: Omit<AnimationEvent, 'type'>): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        callback({ type: event as any, ...data });
      });
    }
  }

  private generateId(): string {
    return `animation-${this.nextId++}`;
  }
}